import { getCustomerDashboardApi } from '../../services/api';

// ─── Date helpers ─────────────────────────────────────────────────────────────

// "2026-01-30" → "Jan 30, 2026"
function formatContractDate(isoDate) {
  if (!isoDate) return '—';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = MONTHS[parseInt(parts[1], 10) - 1];
  const day   = parseInt(parts[2], 10);
  return `${month} ${day}, ${parts[0]}`;
}

// filter: { from: "2026-01-01", to: "2026-05-31" } → "Jan 1, 2026 – May 31, 2026"
function formatFilterLabel(filter) {
  if (!filter?.from && !filter?.to) return 'last 6 months';
  const from = filter.from ? formatContractDate(filter.from) : '';
  const to   = filter.to   ? formatContractDate(filter.to)   : '';
  if (from && to) return `${from} – ${to}`;
  return from || to;
}

// ─── Transform helpers ────────────────────────────────────────────────────────

function transformSummary(d) {
  const { organisation, account_owner, deal, usage } = d || {};
  const hasDeal = !!deal;

  const videosUsed     = usage?.videos_used ?? 0;
  const videosTotal    = hasDeal ? deal.videos_in_contract : null;
  const videosRemaining = usage?.videos_remaining ?? (videosTotal != null ? videosTotal - videosUsed : null);

  const qtUsed      = usage?.quick_turns_used ?? 0;
  const qtTotal     = hasDeal ? deal.quick_turns_in_contract : null;
  const qtRemaining = usage?.quick_turns_remaining ?? (qtTotal != null ? qtTotal - qtUsed : null);

  const creditBased       = deal?.credit_based ?? false;
  const creditsTotal      = hasDeal ? (deal.video_credits ?? null) : null;
  const creditsUsed       = usage?.credits_used ?? 0;
  const creditsRemaining  = usage?.credits_remaining ?? null;

  return {
    org_name:               organisation?.name,
    renews_in_days:         deal?.renews_in_days ?? null,
    renewal_date:           hasDeal ? formatContractDate(deal.contract_end) : 'NA',
    contract_start:         hasDeal ? formatContractDate(deal.contract_start) : 'NA',
    contract_end:           hasDeal ? formatContractDate(deal.contract_end) : 'NA',
    contract_type:          deal?.deal_name || 'NA',
    credit_based:           creditBased,
    videos_total:           videosTotal,
    videos_used:            videosUsed,
    videos_remaining:       videosRemaining,
    videos_pct_used:        usage?.videos_pct_used ?? null,
    quick_turns_total:      qtTotal,
    quick_turns_used:       qtUsed,
    quick_turns_remaining:  qtRemaining,
    quick_turns_pct_used:   usage?.quick_turns_pct_used ?? null,
    credits_total:          creditsTotal,
    credits_used:           creditsUsed,
    credits_remaining:      creditsRemaining,
    credits_pct_used:       usage?.credits_pct_used ?? null,
    revisions_per_video:    deal?.revisions_per_video ?? null,
    video_length_mins:      deal?.video_length_mins ?? null,
    video_credit_duration:  deal?.max_video_length_mins
                              ? `up to ${deal.max_video_length_mins} mins`
                              : 'NA',
    account_owner_name:     account_owner?.name,
    account_owner_email:    account_owner?.email,
    support_email:          'support@lucihub.com',
  };
}

function transformMonthlyOutput(monthly_output) {
  if (!monthly_output?.length) return { months: [], total_delivered: 0 };

  // API shape: { month: "2026-01", label: "Jan 2026", videos, quick_turns }
  // Component expects: { month: "Jan", year: 2026, videos, quick_turns }
  const months = monthly_output.map((m) => {
    const label = m.label || '';
    const [monthStr, yearStr] = label.split(' ');
    return {
      month:       monthStr || m.month,
      year:        yearStr  ? parseInt(yearStr, 10) : null,
      videos:      m.videos,
      quick_turns: m.quick_turns,
    };
  });

  const total_delivered = months.reduce(
    (sum, m) => sum + (m.videos || 0) + (m.quick_turns || 0), 0
  );
  return { months, total_delivered };
}

function transformTeamBreakdown(videos_by_team, filter) {
  if (!videos_by_team?.length) {
    return { teams: [], total: 0, period_label: formatFilterLabel(filter) };
  }
  const teams = videos_by_team.map((t) => ({ name: t.team_name, count: t.count }));
  const total = teams.reduce((sum, t) => sum + (t.count || 0), 0);
  return { teams, total, period_label: formatFilterLabel(filter) };
}

function transformStageBreakdown(stage_breakdown) {
  if (!stage_breakdown?.length) return null;
  const map = {};
  stage_breakdown.forEach((s) => {
    map[s.stage_id] = { stage: s.stage, order: s.order, count: s.count };
  });
  return {
    stages: stage_breakdown
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ stage_id: s.stage_id, stage: s.stage, count: s.count })),
    total: stage_breakdown.reduce((sum, s) => sum + (s.count || 0), 0),
  };
}

// Colors assigned from a fixed palette — API does not provide per-type colors.
const VIDEO_TYPE_COLORS = ['#00D744', '#111613', '#00CEFF', '#282831', '#a0c8b0', '#f0a040'];

function transformVideoTypes(video_types, filter) {
  if (!video_types?.length) {
    return { video_types: [], total: 0, period_label: formatFilterLabel(filter) };
  }
  const types = video_types.map((t, i) => ({
    name:  t.event_type,
    count: t.count,
    color: VIDEO_TYPE_COLORS[i % VIDEO_TYPE_COLORS.length],
  }));
  const total = types.reduce((sum, t) => sum + (t.count || 0), 0);
  return { video_types: types, total, period_label: formatFilterLabel(filter) };
}

// ─── Main fetch ───────────────────────────────────────────────────────────────

// Fetches the dashboard dataset and transforms the response into the shape
// each component expects. Optional params: { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
export const fetchCustomerDashboardData = async (params = {}) => {
  const res = await getCustomerDashboardApi(
    Object.keys(params).length ? params : undefined
  );

  // Real API returns data at res.data (axios wraps body in .data)
  const d = res?.data;

  return {
    summary:       transformSummary(d),
    projectStatus: transformStageBreakdown(d?.stage_breakdown),
    monthlyOutput: transformMonthlyOutput(d?.monthly_output),
    teamBreakdown: transformTeamBreakdown(d?.videos_by_team, d?.filter),
    videoTypes:    transformVideoTypes(d?.video_types, d?.filter),
    // Preserved for future use (attachment download, etc.)
    attachments:   d?.attachments || [],
    filter:        d?.filter || null,
  };
};
